import { Suspense, useEffect } from 'react'

import { Button } from 'src/shared/components/ui/buttons/button'
import { useIsCurrentUser } from 'src/shared/hooks/use-is-current-user'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { cn } from 'src/shared/utils/cn'

import { useUserReviews } from '../../api/marketplace/use-user-reviews'
import { useUserTransactions } from '../../api/marketplace/use-user-transactions'
import { useUserPrivacyPreferences } from '../../api/preferences/use-user-privacy-preferences'

import PublicProfileSectionHeaderSkeleton from './section-header-skeleton'
import { usePublicProfileStore } from './stores/use-public-profile-store'

const PublicProfileSectionHeaderContent = ({ userId }: { userId: number }) => {
  const isCurrentUser = useIsCurrentUser(userId)
  const { activeTab, setActiveTab } = usePublicProfileStore()
  const { data: preferences } = useUserPrivacyPreferences(userId)
  const { data: reviews } = useUserReviews(userId)
  const { data: transactionData } = useUserTransactions(userId)
  const showReviews = ((reviews?.totalReviews || 0) > 0 && (reviews?.reviews.length || 0) > 0) || isCurrentUser
  const showActivity = preferences?.postsVisibleToConsumer
  const showTransactions =
    (preferences?.transactionsVisibleToConsumer && transactionData?.transactions.length > 0) || isCurrentUser
  const isMobile = useIsMobile()

  useEffect(() => {
    // Check if current active tab is available
    const isActiveTabAvailable =
      (activeTab === 'activity' && showActivity) ||
      (activeTab === 'reviews' && showReviews) ||
      (activeTab === 'transactions' && showTransactions)

    if (!isActiveTabAvailable) {
      // Find the first available tab
      if (showActivity) {
        setActiveTab('activity')
      } else if (showReviews) {
        setActiveTab('reviews')
      } else if (showTransactions) {
        setActiveTab('transactions')
      }
    }
  }, [showActivity, showReviews, showTransactions, activeTab, setActiveTab])

  return (
    <div className="relative flex w-full items-start justify-start bg-background py-2">
      <div className="flex w-full items-start justify-start gap-4 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {showActivity && (
          <Button
            className={cn(
              'border border-primary/10 bg-white px-6 text-primary md:px-8',
              activeTab === 'activity' && 'bg-primary text-white',
            )}
            onClick={() => setActiveTab('activity')}
            size={isMobile ? 'sm' : 'default'}
          >
            Activity
          </Button>
        )}
        {showReviews && (
          <Button
            className={cn(
              'border border-primary/10 bg-white px-6 text-primary md:px-8',
              activeTab === 'reviews' && 'bg-primary text-white',
            )}
            onClick={() => setActiveTab('reviews')}
            size={isMobile ? 'sm' : 'default'}
          >
            Reviews
          </Button>
        )}
        {showTransactions && (
          <Button
            className={cn(
              'border border-primary/10 bg-white px-6 text-primary md:px-8',
              activeTab === 'transactions' && 'bg-primary text-white',
            )}
            onClick={() => setActiveTab('transactions')}
            size={isMobile ? 'sm' : 'default'}
          >
            Transactions
          </Button>
        )}
      </div>
    </div>
  )
}

const PublicProfileSectionHeader = ({ userId }: { userId: number }) => {
  return (
    <Suspense fallback={<PublicProfileSectionHeaderSkeleton />}>
      <PublicProfileSectionHeaderContent userId={userId} />
    </Suspense>
  )
}

export default PublicProfileSectionHeader
